import { useAmplitude } from '@risksmart-app/components/src/segment';
import { useEffect } from 'react';

import type { RenderSignal } from './knockContent';
import {
  buildRenderEventProperties,
  NOTIFICATION_FEED_ITEM_RENDERED_EVENT,
} from './notificationRenderTelemetry';

// Module-level so a feed item is counted once per browser session even as the notification
// dropdown (and this hook) mounts/unmounts on open/close. Bounded by the feed page size.
const emittedItemIds = new Set<string>();

/** Test-only: clear the per-session dedup set between cases. */
export const __resetNotificationRenderTelemetry = (): void => {
  emittedItemIds.clear();
};

/**
 * Emit one cutover-telemetry event per feed item the first time it is rendered in a session
 * (RSP-6347), via the app's existing Amplitude client — no new analytics SDK. Inherits
 * Amplitude's env-gating: with no API key the provider never initialises, so events queue
 * harmlessly and local/ephemeral envs no-op. Fire-and-forget — the result is never awaited,
 * so a telemetry failure can never break the feed (matches `useBaseTracking`).
 */
export const useNotificationRenderTelemetry = (
  signals: RenderSignal[]
): void => {
  const amplitude = useAmplitude();

  useEffect(() => {
    const now = Date.now();

    for (const signal of signals) {
      if (emittedItemIds.has(signal.id)) {
        continue;
      }
      emittedItemIds.add(signal.id);

      void amplitude.track({
        event_type: NOTIFICATION_FEED_ITEM_RENDERED_EVENT,
        event_properties: buildRenderEventProperties(signal, now),
      });
    }
  }, [signals, amplitude]);
};
